import type { TaskConfig, TaskMode } from './schemaTypes';
import type { TaskConditionRule, TaskRuntimeContext } from './types';
import { clientLogger } from '../utils/logger';

const matchesRule = (
  rule: TaskConditionRule,
  taskRuntimeContext?: TaskRuntimeContext,
) => {
  switch (rule.field) {
    case 'gift':
      return (
        rule.operator === 'equals' && taskRuntimeContext?.gift === rule.value
      );
    default:
      clientLogger.logError(
        new Error(
          `Unsupported task condition field: ${JSON.stringify(rule.field)}`,
        ),
        'Unsupported task condition rule',
        rule,
      );
      return false;
  }
};

const matchesConditions = (
  task: TaskConfig,
  taskRuntimeContext?: TaskRuntimeContext,
) => {
  if (!task.conditions) {
    return true;
  }

  const allPasses =
    !task.conditions.all ||
    task.conditions.all.every((rule) => matchesRule(rule, taskRuntimeContext));
  const anyPasses =
    !task.conditions.any ||
    task.conditions.any.some((rule) => matchesRule(rule, taskRuntimeContext));

  return allPasses && anyPasses;
};

export const getEffectiveMode = (
  task: TaskConfig,
  taskRuntimeContext?: TaskRuntimeContext,
): TaskMode => {
  if (!matchesConditions(task, taskRuntimeContext)) {
    return 'disabled';
  }

  return task.mode;
};

/**
 * Whether a task takes part in the current form as a field of its own.
 *
 * Its three modes make this the same question as "does the shopper see it": an implicit
 * task's data is carried without being rendered or validated here, and a disabled one is
 * absent entirely. Written as an equality rather than the two exclusions it replaces,
 * which read as if a fourth mode might exist.
 */
export const isTaskEnabled = (
  task: TaskConfig,
  taskRuntimeContext?: TaskRuntimeContext,
): boolean => getEffectiveMode(task, taskRuntimeContext) === 'enabled';
